// Mock API service simulating Firebase Firestore structure
// Collection: daily_puzzles / Document: YYYY-MM-DD / Fields: easy, medium, hard (maps)

interface PuzzleData {
  difficulty: string;
  fen: string;
  first_move: string;
  result: string;
}

interface DailyPuzzles {
  easy: PuzzleData;
  medium: PuzzleData;
  hard: PuzzleData;
}

// Sample puzzles for different dates
const mockPuzzlesDatabase: Record<string, DailyPuzzles> = {
  '2025-12-01': {
    easy: {
      difficulty: 'easy',
      fen: '8/5k2/8/p2N1P2/P3K1pp/1P6/3b2P1/8 w - - 4 50',
      first_move: 'e4d3',
      result: 'win'
    },
    medium: {
      difficulty: 'medium',
      fen: '4R3/5k2/R7/7p/4P1pP/1KPn1p2/6r1/8 w - - 4 46',
      first_move: 'e8h8',
      result: 'win'
    },
    hard: {
      difficulty: 'hard',
      fen: '8/2kp4/1p6/2p1p3/P3P3/7P/6P1/6K1 b - - 1 42',
      first_move: 'd7d5',
      result: 'win'
    }
  },
  '2025-11-30': {
    easy: {
      difficulty: 'easy',
      fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 4 4',
      first_move: 'e1g1',
      result: 'win'
    },
    medium: {
      difficulty: 'medium',
      fen: '2kr3r/ppp2ppp/2n1bn2/3qp3/3P4/2NBPN2/PPP2PPP/R1BQK2R w KQ - 0 8',
      first_move: 'e1g1',
      result: 'win'
    },
    hard: {
      difficulty: 'hard',
      fen: 'r1bq1rk1/ppp2ppp/2np1n2/2b1p3/2B1P3/2NP1N2/PPP2PPP/R1BQK2R w KQ - 0 7',
      first_move: 'e1g1',
      result: 'win'
    }
  },
  '2024-12-01': {
    easy: {
      difficulty: 'easy',
      fen: '8/5k2/8/p2N1P2/P3K1pp/1P6/3b2P1/8 w - - 4 50',
      first_move: 'e4d3',
      result: 'win'
    },
    medium: {
      difficulty: 'medium',
      fen: '4R3/5k2/R7/7p/4P1pP/1KPn1p2/6r1/8 w - - 4 46',
      first_move: 'e8h8',
      result: 'win'
    },
    hard: {
      difficulty: 'hard',
      fen: '8/2kp4/1p6/2p1p3/P3P3/7P/6P1/6K1 b - - 1 42',
      first_move: 'd7d5',
      result: 'win'
    }
  },
  '2024-11-30': {
    easy: {
      difficulty: 'easy',
      fen: 'r3k2r/p1ppqpb1/bn2pnp1/3PN3/1p2P3/2N2Q1p/PPPBBPPP/R3K2R w KQkq - 0 1',
      first_move: 'e1g1',
      result: 'win'
    },
    medium: {
      difficulty: 'medium',
      fen: 'rnbqkb1r/pp1ppppp/5n2/2p5/2P5/5N2/PP1PPPPP/RNBQKB1R w KQkq - 0 3',
      first_move: 'd2d4',
      result: 'win'
    },
    hard: {
      difficulty: 'hard',
      fen: 'r1bqkbnr/pppp1ppp/2n5/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 2 3',
      first_move: 'f1c4',
      result: 'win'
    }
  }
};

export const fetchPuzzlesByDate = async (date: string): Promise<DailyPuzzles> => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  if (backendUrl) {
    try {
      console.log(`Fetching puzzles from backend: ${backendUrl}/api/puzzles/${date}`);

      // Use efficient timeout to avoid hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout

      const response = await fetch(`${backendUrl}/api/puzzles/${date}`, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        console.log('Backend response:', data);
        if (data && typeof data === 'object') {
          return data;
        }
      } else {
        console.warn(`Backend fetch failed with status ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching from backend:', error);
    }
  }

  // Simulate network delay for mock fallback
  await new Promise(resolve => setTimeout(resolve, 300));

  // Check if we have puzzles for this date
  if (mockPuzzlesDatabase[date]) {
    return mockPuzzlesDatabase[date];
  }

  // Generate default puzzles for dates we don't have
  return generateDefaultPuzzles(date);
};

function generateDefaultPuzzles(date: string): DailyPuzzles {
  // Generate deterministic puzzles based on date
  // const seed = date.split('-').reduce((acc, val) => acc + parseInt(val), 0);

  return {
    easy: {
      difficulty: 'easy',
      fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
      first_move: 'e2e4',
      result: 'win'
    },
    medium: {
      difficulty: 'medium',
      fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
      first_move: 'd2d4',
      result: 'win'
    },
    hard: {
      difficulty: 'hard',
      fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
      first_move: 'g1f3',
      result: 'win'
    }
  };
}

export default fetchPuzzlesByDate;
